import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    ArrowLeftIcon,
    BriefcaseIcon,
    ShareIcon,
    EyeIcon,
    KeyIcon,
    CreditCardIcon,
    BuildingLibraryIcon,
    UserIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import AdminService from '../../services/admin.service';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';

interface Category {
    id: string;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    password_count: number;
    card_count: number;
    bank_count: number;
    personal_count: number;
    total_count: number;
}

type NavigationLevel = 'root' | 'category';

const AdminCategoriesPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();

    const [currentLevel, setCurrentLevel] = useState<NavigationLevel>('root');
    const [categories, setCategories] = useState<Category[]>([]);
    const [unassignedCounts, setUnassignedCounts] = useState<any>({});
    const [totalUnassigned, setTotalUnassigned] = useState(0);
    const [userEmail, setUserEmail] = useState('');
    const [vaultName, setVaultName] = useState('');
    const [vaultId, setVaultId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (userId) {
            loadCategories();
        }
    }, [userId]);

    const loadCategories = async () => {
        try {
            setLoading(true);
            console.log('Loading categories for user:', userId);

            const data = await AdminService.getUserCategories(userId!);

            setCategories(data.categories);
            setUnassignedCounts(data.unassignedCounts);
            setTotalUnassigned(data.totalUnassigned);
            setUserEmail(data.userEmail);
            setVaultName(data.vaultName);
            setVaultId(data.vaultId);

            toast.success('Categories loaded successfully');
        } catch (error: any) {
            console.error('Error loading categories:', error);
            const errorMessage = error.message || 'Failed to load categories';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryClick = (categoryId: string) => {
        // Navigate to category items page
        navigate(`/admin/user/${userId}/category/${categoryId}`);
    };

    const handleBackClick = () => {
        navigate('/manage-members');
    };

    const hasCategories = categories.length > 0 || totalUnassigned > 0;

    return (
        <div className="h-screen flex overflow-hidden bg-gray-100">
            <VaultSidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {/* Loading State */}
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="relative">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200"></div>
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent absolute top-0 left-0"></div>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mt-4 text-lg font-medium">Loading vaults...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {!loading && error && (
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                <h3 className="text-red-800 font-semibold mb-2">Error</h3>
                                <p className="text-red-600">{error}</p>
                                <button
                                    onClick={handleBackClick}
                                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Back to Team Members
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    {!loading && !error && (
                        <>
                            {/* Hero Banner */}
                            <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                                {/* Background Pattern */}
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                                    <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                                    <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                                </div>

                                <div className="relative px-6 py-12">
                                    <div className="max-w-7xl mx-auto">
                                        {/* Back Button */}
                                        <button
                                            onClick={handleBackClick}
                                            className="mb-6 inline-flex items-center text-white hover:text-white/80 transition-colors"
                                        >
                                            <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                            Back to Team Members
                                        </button>

                                        {/* Header Content */}
                                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                                            <div className="mb-6 lg:mb-0">
                                                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                                                    {userEmail}'s Vaults
                                                </h1>
                                                <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                                                    Super Admin View - Read-only access to {vaultName}
                                                </p>
                                                {/* Warning Badge */}
                                                <div className="mt-4 inline-flex items-center px-4 py-2 bg-yellow-900/30 border border-yellow-500/30 rounded-lg">
                                                    <EyeIcon className="h-5 w-5 text-yellow-300 mr-2" />
                                                    <span className="text-yellow-300 font-medium">Read-Only Mode</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats Cards */}
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                                <div className="flex items-center space-x-3">
                                                    <div className="p-2 bg-white/20 rounded-lg">
                                                        <BriefcaseIcon className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="text-2xl font-bold text-white">
                                                            {categories.length + (totalUnassigned > 0 ? 1 : 0)}
                                                        </div>
                                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                                            Total Vaults
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                                <div className="flex items-center space-x-3">
                                                    <div className="p-2 bg-white/20 rounded-lg">
                                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="text-2xl font-bold text-white">
                                                            {categories.reduce((sum, cat) => sum + cat.total_count, 0) + totalUnassigned}
                                                        </div>
                                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                                            Total Items
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                                <div className="flex items-center space-x-3">
                                                    <div className="p-2 bg-white/20 rounded-lg">
                                                        <EyeIcon className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="text-2xl font-bold text-white">
                                                            VIEW
                                                        </div>
                                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                                            Access Mode
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="px-4 sm:px-6 lg:px-8 py-8">
                                {/* Empty State */}
                                {!hasCategories && (
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-12 text-center">
                                        <BriefcaseIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                            No Vaults Found
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            This user has no vaults or items yet.
                                        </p>
                                    </div>
                                )}

                                {/* Vault Cards Grid */}
                                {hasCategories && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {/* Unassigned Vault */}
                                        {totalUnassigned > 0 && (
                                            <div
                                                onClick={() => handleCategoryClick('uncategorized')}
                                                className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-2xl border border-gray-200 dark:border-gray-700 hover:border-red-300 transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                                            >
                                                <div className="relative p-6 lg:p-8">
                                                    <div className="flex items-start justify-between mb-6">
                                                        <div className="flex items-center space-x-4 min-w-0 flex-1">
                                                            <div className="relative">
                                                                <div className="h-14 w-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-500 dark:text-gray-400 group-hover:scale-110 transition-all duration-300 shadow-lg">
                                                                    <BriefcaseIcon className="h-7 w-7" />
                                                                </div>
                                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-sm">
                                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                                </div>
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                                                                    Unassigned
                                                                </h3>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                                                    Items awaiting organization
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 shadow-sm">
                                                                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                                                                {totalUnassigned} {totalUnassigned === 1 ? 'item' : 'items'}
                                                            </span>
                                                        </div>

                                                        <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-x-2">
                                                            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                                                                <ChevronRightIcon className="h-4 w-4 text-white" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Category Vaults */}
                                        {categories.map((category) => {
                                            const colorVariants = [
                                                { from: 'from-blue-500', to: 'to-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
                                                { from: 'from-purple-500', to: 'to-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
                                                { from: 'from-green-500', to: 'to-green-600', bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
                                                { from: 'from-orange-500', to: 'to-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
                                                { from: 'from-pink-500', to: 'to-pink-600', bg: 'bg-pink-50 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400' },
                                                { from: 'from-indigo-500', to: 'to-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' }
                                            ];
                                            const colorIndex = category.name.charCodeAt(0) % colorVariants.length;
                                            const colors = colorVariants[colorIndex];

                                            return (
                                                <div
                                                    key={category.id}
                                                    onClick={() => handleCategoryClick(category.id)}
                                                    className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-2xl border border-gray-200 dark:border-gray-700 hover:border-red-300 transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                                                >
                                                    <div className="relative p-6 lg:p-8">
                                                        <div className="flex items-start justify-between mb-6">
                                                            <div className="flex items-center space-x-4 min-w-0 flex-1 mr-4">
                                                                <div className="relative">
                                                                    <div className={`h-14 w-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${colors.from} ${colors.to} group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                                                                        <BriefcaseIcon className="h-7 w-7 text-white" />
                                                                    </div>

                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                <h3 className={`text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:${colors.text.split(' ')[0]} transition-colors duration-300 truncate`} title={category.name}>
                                                                    {category.name}
                                                                </h3>
                                                                {category.description && (
                                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 leading-relaxed" title={category.description}>
                                                                        {category.description}
                                                                    </p>
                                                                )}
                                                                
                                                            </div>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 shadow-sm">
                                                                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                                                                {category?.total_count} {category?.total_count === 1 ? 'item' : 'items'}
                                                            </span>
                                                        </div>

                                                        <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-x-2">
                                                            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                                                                <ChevronRightIcon className="h-4 w-4 text-white" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default AdminCategoriesPage;
